import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import mascot from "../../Assets/Images/mascot.png";
import logo from "../../Assets/Images/logo.png";
import "bootstrap/dist/css/bootstrap.css";
import { notification } from "antd";
import Loader from "../../pages/Loader";
import config, { axiosInstance, checkLoginStatus } from "../../pages/Config";
import secureLocalStorage from "react-secure-storage";
import { usePermissionData } from "../../AppContext";
import swal from "sweetalert";

// module_id values match the permission matrix in AddRole.js:
// 1=Dashboard, 2=Videos, 3=Generated Images, 4=Generated Videos, 5=AI Chats,
// 6=Staff, 7=Roles & Permissions, 8=Audit Logs, 9=Users,
// 10=AI Token Limits, 11=Manage AI Keys, 12=Restricted Words,
// 13=Privacy Policy, 14=Category, 15=Tutors, 18=Payments
const menuItems = [
  {
    text: "Dashboard",
    url: `${config.appUrl}`,
    icon: "icon-home",
  },
  {
    text: "Users",
    url: `${config.appUrl}users`,
    icon: "bi bi-people",
    module_id: 9,
  },
  {
    text: "Videos",
    url: `${config.appUrl}videos`,
    icon: "bi bi-camera-video",
    module_id: 2,
  },
  {
    text: "AI Generated",
    icon: "bi bi-stars",
    children: [
      {
        text: "Generated Images",
        url: `${config.appUrl}ai-generated/images`,
        icon: "bi bi-image",
        module_id: 3,
      },
      {
        text: "Generated Videos",
        url: `${config.appUrl}ai-generated/videos`,
        icon: "bi bi-film",
        module_id: 4,
      },
    ],
  },
  {
    text: "AI Chats",
    url: `${config.appUrl}ai-chats`,
    icon: "bi bi-chat-dots",
    module_id: 5,
  },
  {
    text: "Tutor",
    url: `${config.appUrl}tutors`,
    icon: "bi bi-person-video3",
    module_id: 15,
  },
  {
    text: "Staff",
    url: `${config.appUrl}staff`,
    icon: "icon-users2",
    module_id: 6,
  },
  {
    text: "Roles & Permissions",
    url: `${config.appUrl}roles`,
    icon: "icon-user-lock",
    module_id: 7,
  },
  {
    text: "Audit Logs",
    url: `${config.appUrl}audit`,
    icon: "icon-files",
    module_id: 8,
  },
  {
    text: "Payments",
    url: `${config.appUrl}payments`,
    icon: "bi bi-credit-card",
    module_id: 18,
  },
  {
    text: "Settings",
    icon: "bi bi-gear",
    children: [
      {
        text: "Plans & Subscriptions",
        url: `${config.appUrl}settings/plans`,
        icon: "bi bi-journal-bookmark",
        module_id: 17,
      },
      {
        text: "AI Token Limits",
        url: `${config.appUrl}settings/ai-token-limits`,
        icon: "bi bi-cpu",
        module_id: 10,
      },
      {
        text: "Manage AI Keys",
        url: `${config.appUrl}settings/manage-ai-keys`,
        icon: "bi bi-key",
        module_id: 11,
      },
      {
        text: "Restricted Words",
        url: `${config.appUrl}settings/restricted-words`,
        icon: "bi bi-slash-circle",
        module_id: 12,
      },
      {
        text: "Privacy Policy",
        url: `${config.appUrl}settings/privacy-policy`,
        icon: "bi bi-shield-lock",
        module_id: 13,
      },
      {
        text: "Category",
        url: `${config.appUrl}settings/category`,
        icon: "bi bi-grid",
        module_id: 14,
      },
      {
        text: "Creator",
        url: `${config.appUrl}settings/creators`,
        icon: "bi bi-person-video",
        module_id: 16,
      },
    ],
  },
];

const ContainerDashboard = ({ children, title, description }) => {
  const [isLoading, setLoading] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  const credential = secureLocalStorage.getItem("admin_credentials");
  const { setLoginStatus, checkAccess, clearPermissions } = usePermissionData();

  const location = useLocation();
  const navigate = useNavigate();

  const isChildActive = (item) =>
    item.children?.some((child) => location.pathname === child.url) ?? false;

  const [openMenus, setOpenMenus] = useState(() => {
    const open = {};
    menuItems.forEach((item) => {
      if (item.children?.some((child) => location.pathname === child.url)) {
        open[item.text] = true;
      }
    });
    return open;
  });

  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.children?.some((child) => location.pathname === child.url)) {
        setOpenMenus((prev) => ({ ...prev, [item.text]: true }));
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    if (checkLoginStatus(credential)) {
      navigate(`${config.appUrl}login`);
    }
  }, []);

  const toggleMenu = (text) => {
    setOpenMenus((prev) => ({ ...prev, [text]: !prev[text] }));
  };

  const canViewItem = (item) => {
    if (!item.module_id) return true;
    return checkAccess(item.module_id, 1);
  };

  const getVisibleChildren = (children) =>
    children.filter((child) => canViewItem(child));

  const onLogout = (e) => {
    e.preventDefault();

    swal({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willdelete) => {
      if (willdelete) {
        let logoutMessage = "Your account has been logged out successfully.";
        try {
          const response = await axiosInstance.post("auth/logout");
          if (response?.data?.message) logoutMessage = response.data.message;
        } catch (_) {
          // proceed with local logout even if API call fails
        }
        secureLocalStorage.removeItem("admin_credentials");
        clearPermissions();
        setLoginStatus(false);

        notification.open({
          message: (
            <div className="d-flex">
              <div className="pr-20">
                <i
                  className="icon-warning text-danger"
                  style={{ fontSize: "20px" }}
                ></i>
              </div>
              <div>Thank You!</div>
            </div>
          ),
          description: logoutMessage,
        });
        navigate(`${config.appUrl}login`);
      }
    });
  };

  return (
    <div className="martfury-admin">
      {isLoading && <Loader />}

      {!mobileMenu && (
        <div className="header--mobile">
          <div className="header__left">
            <button
              className="ps-drawer-toggle"
              onClick={() => setMobileMenu(true)}
            >
              <i className="bi bi-list" style={{ fontSize: "3.5rem" }}></i>
            </button>
          </div>
          <div className="header__center">
            <Link to={`${config.appUrl}`}>
              <img src={logo} alt="Mindora" style={{ height: "80px" }} />
            </Link>
          </div>
          <div className="header__right">
            <a onClick={onLogout} style={{ cursor: "pointer" }}>
              <i
                className="bi bi-box-arrow-right"
                style={{ fontSize: "3.5rem" }}
              ></i>
            </a>
          </div>
        </div>
      )}

      {mobileMenu && (
        <div
          className="ps-sidebar-backdrop"
          onClick={() => setMobileMenu(false)}
        />
      )}

      <main className="ps-main">
        <div
          className={`ps-main__sidebar pt-0 ${mobileMenu ? "show-menu" : ""}`}
          style={{
            width: "270px",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "fixed",
            top: 0,
            left: 0,
          }}
        >
          <div
            className="d-none d-xl-block ps-sidebar__top py-4"
            style={{ flexShrink: 0, zIndex: 10 }}
          >
            <div className="text-center">
              <Link
                to={`${config.appUrl}`}
                className="d-flex align-items-center justify-content-center gap-2 text-decoration-none"
              >
                <img
                  src={logo}
                  alt="Mindora"
                  style={{
                    width: "300px",
                    height: "48px",
                    objectFit: "contain",
                  }}
                />
              </Link>
            </div>
          </div>

          <div
            className="d-xl-none d-flex align-items-center px-3 py-3"
            style={{
              flexShrink: 0,
              borderBottom: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <i
              className="bi bi-x-lg text-white fs-3"
              onClick={() => setMobileMenu(false)}
              style={{ cursor: "pointer" }}
            ></i>
          </div>

          <hr style={{ color: "white", margin: "0 0 8px 0", flexShrink: 0 }} />

          <div
            className="ps-sidebar__center"
            style={{
              flex: "1 1 0",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <style>{`.ps-sidebar__center::-webkit-scrollbar { display: none; }`}</style>
            <ul className="menu">
              {menuItems.map((item, index) => {
                if (item.children) {
                  const visibleChildren = getVisibleChildren(item.children);
                  if (visibleChildren.length === 0) return null;
                  return (
                    <li key={index}>
                      <a
                        onClick={() => toggleMenu(item.text)}
                        className={`d-flex align-items-center ${
                          isChildActive(item) ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        <i className={item.icon}></i>
                        <span className="flex-grow-1">{item.text}</span>
                        <i
                          className={`bi ${
                            openMenus[item.text]
                              ? "bi-chevron-up"
                              : "bi-chevron-down"
                          }`}
                          style={{
                            fontSize: "11px",
                            opacity: 0.7,
                            marginLeft: "auto",
                          }}
                        ></i>
                      </a>
                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          margin: 0,
                          overflow: "hidden",
                          maxHeight: openMenus[item.text] ? "400px" : "0",
                          transition: "max-height 0.3s ease",
                        }}
                      >
                        {visibleChildren.map((child, ci) => (
                          <li key={ci} style={{ paddingLeft: "20px" }}>
                            <Link
                              to={child.url}
                              className={
                                location.pathname === child.url ? "active" : ""
                              }
                              style={{ fontSize: "13px" }}
                            >
                              <i
                                className={child.icon}
                                style={{ fontSize: "14px" }}
                              ></i>
                              {child.text}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                } else {
                  if (!canViewItem(item)) return null;
                  return (
                    <li key={index}>
                      <Link
                        to={item.url}
                        className={
                          location.pathname === item.url ? "active" : ""
                        }
                      >
                        <i className={item.icon}></i>
                        {item.text}
                      </Link>
                    </li>
                  );
                }
              })}
            </ul>
          </div>

          <div
            className="pt-3 pb-0"
            style={{
              flexShrink: 0,
              borderTop: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <img src={mascot} alt="Mindora" style={{ width: "70px" }} />
              <div>
                <p className="text-white mb-1">
                  &copy; {new Date().getFullYear()} Mindora.
                </p>
                <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                  Designed &amp; Developed By <br />
                  <a
                    href="https://www.zithas.co.uk"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--hover-color)" }}
                  >
                    Zithas Technologies
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="ps-main__wrapper">
          <header className="header--dashboard">
            <div className="header__left">
              <h3>{title}</h3>
              <p className="text-nowrap">{description}</p>
            </div>
            <div className="header__center"></div>
            <div className="header__right">
              <a
                className="header__site-link"
                onClick={onLogout}
                style={{ cursor: "pointer", fontSize: "14px" }}
              >
                <span>Logout</span>
                <i className="icon-exit-right"></i>
              </a>
            </div>
          </header>

          {children}
        </div>
      </main>
    </div>
  );
};

export default ContainerDashboard;
